import React from 'react';
import ScrollReveal, { StaggerContainer, StaggerItem } from './layout/ScrollReveal';

/**
 * Case study — how Visualte built and runs a powered product.
 * Renders only the blocks present on the `study` object (see lib/caseStudies.js).
 */
export default function CaseStudy({ study, projectName }) {
  if (!study) return null;
  const { brief, gallery, systems, surfaces, work, stack, languages, footnote } = study;

  // Section numbers follow the blocks this study actually has.
  let n = 0;
  const sec = () => `SEC.${String(++n).padStart(2, '0')}`;

  return (
    <>
      {/* ── The brief ── */}
      {brief && (
        <section className="py-12 sm:py-16 relative" data-testid="case-brief-section">
          <div className="absolute inset-0 noise-overlay" />
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-16">
              <ScrollReveal>
                <div className="hud-index mb-3">{sec()} // CASE FILE</div>
                <span className="kicker">The brief</span>
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4">{brief.headline}</h2>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <div className="space-y-4 lg:pt-10">
                  {brief.paragraphs.map((p, i) => (
                    <p key={i} className="text-sm md:text-base text-white/72 leading-relaxed">{p}</p>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      )}

      {/* ── Screens from the live product ── */}
      {gallery && gallery.length > 0 && (
        <section className="py-12 sm:py-16 relative" data-testid="case-gallery-section">
          <div className="absolute inset-0 noise-overlay" />
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <ScrollReveal>
              <div className="hud-index mb-3">{sec()} // FIELD CAPTURE</div>
              <span className="kicker">On screen</span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-4">{study.galleryHeadline || 'The live product'}</h2>
              {study.galleryIntro && <p className="text-sm md:text-base text-white/72 leading-relaxed max-w-xl mb-10">{study.galleryIntro}</p>}
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-5">
              {gallery.map((g, i) => (
                <ScrollReveal key={g.src} delay={Math.min(i, 4) * 0.06} className={GALLERY_LAYOUT[g.layout] || GALLERY_LAYOUT.half}>
                  <figure className="hud-card border border-white/10 bg-white/[0.03] p-2 sm:p-2.5 h-full flex flex-col card-hover-glow">
                    <div className={`relative overflow-hidden rounded-[3px] border border-white/8 bg-black/40 ${g.layout === 'tall' ? 'lg:flex-1' : ''}`}>
                      <img
                        src={g.src}
                        alt={g.alt || `${projectName}: ${g.label}`}
                        width={g.w}
                        height={g.h}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                        className={g.layout === 'tall'
                          ? 'block w-full h-auto max-w-[340px] mx-auto lg:max-w-none lg:absolute lg:inset-0 lg:h-full lg:object-cover lg:object-top'
                          : 'block w-full h-auto'}
                      />
                    </div>
                    <figcaption className="px-1.5 pt-3 pb-1.5">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="font-mono text-[10px] tracking-[0.18em] text-[#E8722A]/80 uppercase">{g.label}</span>
                        <span className="font-mono text-[10px] text-white/30 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                      </div>
                      {g.caption && <p className="text-xs sm:text-sm text-white/55 leading-relaxed">{g.caption}</p>}
                    </figcaption>
                  </figure>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── System map ── */}
      {systems && systems.length > 0 && (
        <section className="py-12 sm:py-16 relative" data-testid="case-systems-section">
          <div className="absolute inset-0 bg-ember-glow" />
          <div className="absolute inset-0 noise-overlay" />
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <ScrollReveal>
              <div className="hud-index mb-3">{sec()} // SYSTEM MAP</div>
              <span className="kicker">Connected systems</span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-4">One operating environment</h2>
              <p className="text-sm md:text-base text-white/72 leading-relaxed max-w-xl mb-10">
                {study.systemsIntro || `Every module in ${projectName} connects to the one beside it. Activate what the business needs. The rest is ready.`}
              </p>
            </ScrollReveal>
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {systems.map((sys, i) => (
                <StaggerItem key={sys.id}>
                  <div className="hud-card border border-white/10 bg-white/[0.03] p-5 h-full card-hover-glow">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-[10px] tracking-[0.18em] text-[#E8722A]/80 uppercase">{sys.id}</span>
                      <span className="font-mono text-[10px] text-white/30 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <h3 className="text-base font-semibold tracking-[-0.02em] mb-1.5">{sys.name}</h3>
                    <p className="text-sm text-white/55 leading-relaxed">{sys.desc}</p>
                    {sys.links && sys.links.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/6 font-mono text-[10px] tracking-[0.1em] text-white/35 uppercase">
                        → {sys.links.join(' · ')}
                      </div>
                    )}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      {/* ── What Visualte built ── */}
      {work && work.length > 0 && (
        <section className="py-12 sm:py-16 relative" data-testid="case-work-section">
          <div className="absolute inset-0 noise-overlay" />
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-16">
              <ScrollReveal>
                <div className="lg:sticky lg:top-28">
                  <div className="hud-index mb-3">{sec()} // ENGINEERING</div>
                  <span className="kicker">What Visualte built</span>
                  <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-4">{study.workHeadline || 'The engine underneath'}</h2>
                  {study.workIntro && <p className="text-sm md:text-base text-white/72 leading-relaxed max-w-md">{study.workIntro}</p>}
                </div>
              </ScrollReveal>
              <div className="space-y-0">
                {work.map((w, i) => (
                  <ScrollReveal key={w.title} delay={i * 0.06}>
                    <div className="border-b border-white/8 py-7 first:pt-0 flex flex-col sm:flex-row gap-3 sm:gap-8">
                      <span className="font-mono text-xs text-[#E8722A] mt-1 tabular-nums shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <div>
                        <h3 className="text-lg font-semibold tracking-[-0.02em] mb-2">{w.title}</h3>
                        <p className="text-sm text-white/60 leading-relaxed max-w-xl">{w.body}</p>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Surfaces + stack readout ── */}
      {((surfaces && surfaces.length > 0) || (stack && stack.length > 0)) && (
        <section className="py-12 sm:py-16 relative" data-testid="case-stack-section">
          <div className="absolute inset-0 bg-fire-engine" />
          <div className="absolute inset-0 noise-overlay" />
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <ScrollReveal>
              <div className="hud-index mb-3">{sec()} // READOUT</div>
              <span className="kicker">Surfaces and stack</span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-10">Where it runs</h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6 lg:gap-8">
              {surfaces && surfaces.length > 0 && (
                <ScrollReveal>
                  <div className="hud-card border border-white/10 bg-white/[0.03] p-6 h-full">
                    <span className="kicker block mb-4">Surfaces</span>
                    <div className="divide-y divide-white/6">
                      {surfaces.map((s) => (
                        <div key={s.name} className="py-3 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6">
                          <div className="sm:w-44 shrink-0">
                            <div className="text-sm font-medium text-white/90">{s.name}</div>
                            <div className="font-mono text-[10px] tracking-[0.12em] uppercase text-[#E8722A]/80 mt-0.5">{s.platform}</div>
                          </div>
                          <p className="text-sm text-white/55 leading-relaxed">{s.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </ScrollReveal>
              )}
              {stack && stack.length > 0 && (
                <ScrollReveal delay={0.1} direction="right" distance={20}>
                  <div className="hud-card border border-white/10 bg-white/[0.03] p-6 h-full relative overflow-hidden">
                    <div className="absolute inset-0 scanline-bg" />
                    <div className="relative z-10">
                      <span className="kicker block mb-4">Stack readout</span>
                      <div className="space-y-0">
                        {stack.map((row) => (
                          <div key={row.k} className="flex justify-between items-start gap-4 py-2.5 border-b border-white/6 last:border-0">
                            <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-white/40 pt-0.5 shrink-0">{row.k}</span>
                            <span className={`text-sm font-mono text-right ${row.accent ? 'text-[#E8722A]' : 'text-white/80'}`}>{row.v}</span>
                          </div>
                        ))}
                      </div>
                      {languages && languages.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-white/8">
                          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-white/40 mb-2">Locales · {languages.length}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {languages.map((l) => (
                              <span key={l} className="px-2 py-0.5 rounded-[3px] border border-white/10 bg-white/[0.03] font-mono text-[10px] text-white/60 uppercase">{l}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </ScrollReveal>
              )}
            </div>
            {footnote && (
              <ScrollReveal delay={0.15}>
                <p className="font-mono text-[11px] text-white/35 mt-6 leading-relaxed max-w-2xl">{footnote}</p>
              </ScrollReveal>
            )}
          </div>
        </section>
      )}
    </>
  );
}

// Grid placement per gallery tile (6-column grid on desktop).
const GALLERY_LAYOUT = {
  hero: 'sm:col-span-2 lg:col-span-4',
  tall: 'sm:col-span-2 lg:col-span-2 lg:row-span-2',
  third: 'lg:col-span-2',
  half: 'lg:col-span-3',
};
